// ---------------------------------------------------------------------------
// Series colour — the record first, the palette as fallback (2026-09-02).
//
// Sentinel v1 hard-codes energy-type colours in the chart. Monad stores
// colour AS DATA: EnergySource and SEUCategory records carry a `color`
// attribute the user can edit like any other cell — and until now no
// renderer read it. CartesianRenderer coloured series by index into a fixed
// palette, so electricity was "whatever series #0 gets" and could be two
// colours on one page.
//
// This is the ONE seam. When a series/group dimension is an entity reference,
// ViewHost resolves the referenced records' chips (the same chip seam every
// surface uses: kenning + icon + colour) and the remap attaches the colour
// to the row as `<dim>__color`, beside the `<dim>__ref` id it already keeps.
// A renderer asks `seriesColorFor(rows, dim, key, index)`; a record with its
// own colour wins, everything else takes the palette by index — stable across
// hide/show (cold-read H2), and the same source is the same colour on every
// chart, every dashboard, every tenant.
// ---------------------------------------------------------------------------
import { viewTheme } from './theme'

export type RowLike = Record<string, unknown>

export const paletteColor = (i: number): string =>
  viewTheme.series.palette[Math.max(0, i) % viewTheme.series.palette.length]

/** Attach `<dim>__color` to every row whose `<dim>__ref` has a chip colour. */
export function attachRecordColors<T extends { rows: RowLike[] }>(
  frame: T,
  colorsByDim: Record<string, Record<string, string | null | undefined>>,
): T {
  const dims = Object.keys(colorsByDim)
  if (dims.length === 0) return frame
  return {
    ...frame,
    rows: frame.rows.map((row) => {
      const next: RowLike = { ...row }
      for (const dim of dims) {
        const ref = next[`${dim}__ref`]
        if (typeof ref !== 'string') continue
        const c = colorsByDim[dim][ref]
        if (typeof c === 'string' && c.trim()) next[`${dim}__color`] = c.trim()
      }
      return next
    }),
  }
}

/** The colour for series `key` of dimension `dim`: the record's own colour
 *  when the rows carry one, else the palette at `index`. */
export function seriesColorFor(rows: RowLike[], dim: string | null | undefined, key: string, index: number): string {
  if (dim) {
    for (const r of rows) {
      if (String(r[dim]) === key) {
        const c = r[`${dim}__color`]
        if (typeof c === 'string') return c
      }
    }
  }
  return paletteColor(index)
}

/** The label an unresolved record id gets. The SAME word the kenning remap
 *  uses (engine.tsx::remapReferenceDims) — never the id: a UUID on screen is
 *  the one thing this codebase's UI invariants forbid outright. */
export const UNNAMED = '(unnamed)'

/** SEN-1733: a set-valued column (site ← meters → energy_source) carries
 *  record ids; attach `<col>__chips` — one chip {id, kenning, icon, color}
 *  per id, in the set's order — from the SAME chip seam every surface uses.
 *  A single-hop reference column (site → country_ref) carries ONE id and
 *  gets a one-chip stack — the same shape, so a renderer never branches on
 *  arity. Ids the chip fetch did not return keep a chip labelled `(unnamed)`
 *  with no icon/colour — visible and never dropped, but never a raw UUID in
 *  a circle title or a marker tooltip (cold read 3). The id survives in
 *  `chip.id` for the click seam; decorateFrame is what SAYS how many were
 *  unresolved. */
export type SetChip = { id: string; kenning: string; icon: string | null; color: string | null }
export function attachSetChips<T extends { rows: RowLike[] }>(
  frame: T,
  setColumns: string[],
  chips: Record<string, { kenning: string; icon: string | null; color: string | null }>,
): T {
  if (setColumns.length === 0) return frame
  return {
    ...frame,
    rows: frame.rows.map((row) => {
      const next: RowLike = { ...row }
      for (const col of setColumns) {
        const ids = chipIdsIn(next, col)
        if (!ids) continue
        next[`${col}__chips`] = ids.map((id): SetChip => {
          const c = chips[id]
          return c
            ? { id, kenning: c.kenning, icon: c.icon, color: c.color }
            : { id, kenning: UNNAMED, icon: null, color: null }
        })
      }
      return next
    }),
  }
}

/** The record ids a row carries under `col`, normalised to a list: a set
 *  column holds an array, a single reference holds one string. null when the
 *  row carries neither (a site with no meters). One reader, so attachSetChips
 *  and the "what did not resolve" count can never disagree about what an id is. */
export function chipIdsIn(row: RowLike, col: string): string[] | null {
  const raw = row[col]
  if (Array.isArray(raw)) return raw.map(String)
  if (typeof raw === 'string' && raw) return [raw]
  return null
}
